import React, { useCallback, useEffect, useMemo, useState } from "react"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Switch from "@mui/material/Switch"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import PageContent from "components/common/LayoutComponents/PageContent"
import LoadingScreen from "components/common/LoadingScreen"
import { UsePermissionVerification } from "components/customHooks"
import { PERMISSIONS } from "constants/permission"
import { useSnackbar } from "notistack"
import { addSystemConfigs, editSystemConfigs, getSystemConfigByKey } from "services/systemConfig"
import { verifyResponse } from "utils/httpRequest"
import { BetaFeaturesWrapper } from "./BetaFeatures.style"

const CONFIG_KEY = "feature_flags"

const VOICE_CHAT_UI_FLAG_KEYS = ["voice_chat_ui_v0", "voice_chat_ui_v1", "voice_chat_ui_v2"]

function migrateVoiceChatUiFromLegacy(parsed) {
  const flags = parsed?.flags
  if (!flags || typeof flags !== "object") return
  const hasAnyNew = VOICE_CHAT_UI_FLAG_KEYS.some(
    (k) => Object.prototype.hasOwnProperty.call(flags, k) && flags[k] && typeof flags[k] === "object",
  )
  if (hasAnyNew) return
  const embed = flags.voice_embed_view_cms_enabled
  if (!embed || typeof embed !== "object") return
  flags.voice_chat_ui_v0 = {
    enabledForAllUsers: !embed.enabledForAllUsers,
    enabledForBetaUsers: !embed.enabledForBetaUsers,
  }
  flags.voice_chat_ui_v1 = { enabledForAllUsers: false, enabledForBetaUsers: false }
  flags.voice_chat_ui_v2 = {
    enabledForAllUsers: Boolean(embed.enabledForAllUsers),
    enabledForBetaUsers: Boolean(embed.enabledForBetaUsers),
  }
}

const FEATURE_FLAG_DEFINITIONS = [
  {
    key: "games_section_enabled",
    title: "Games section",
    description:
      "Controls visibility of the Neuro-Fitness Games section on the member dashboard.",
    defaultEnabledForAllUsers: false,
    defaultEnabledForBetaUsers: true,
  },
  {
    key: "games_entitlement_gating_enabled",
    title: "Games entitlement gating",
    description:
      "Requires Gold or Platinum for the full NeuroFitness Games catalog while leaving the free sample unlocked.",
    defaultEnabledForAllUsers: true,
    defaultEnabledForBetaUsers: true,
  },
  {
    key: "badges_enabled",
    title: "Badges",
    description:
      "Controls whether members can see the goals and badges experience tied to games and progress.",
    defaultEnabledForAllUsers: false,
    defaultEnabledForBetaUsers: true,
  },
  {
    key: "ai_study_lab_enabled",
    title: "AI Study Lab",
    description:
      "Controls whether AI Study Lab is available in the course experience.",
    defaultEnabledForAllUsers: false,
    defaultEnabledForBetaUsers: false,
  },
  {
    key: "periodic_reports_enabled",
    title: "Periodic Reports",
    description:
      "Controls whether members can access periodic progress reports.",
    defaultEnabledForAllUsers: false,
    defaultEnabledForBetaUsers: true,
  },
  {
    key: "marketplace_enabled",
    title: "Marketplace",
    description:
      "Controls whether the marketplace is available in the member app.",
    defaultEnabledForAllUsers: true,
    defaultEnabledForBetaUsers: true,
  },
  {
    key: "integrations_enabled",
    title: "Integrations",
    description:
      "Controls whether integrations are available in settings.",
    defaultEnabledForAllUsers: true,
    defaultEnabledForBetaUsers: true,
  },
  {
    key: "favorites_playlists_enabled",
    title: "Favorites and Playlists",
    description:
      "Controls whether favorites and playlists are available in the member app and settings.",
    defaultEnabledForAllUsers: false,
    defaultEnabledForBetaUsers: false,
  },
  {
    key: "app_launch_popup_enabled",
    title: "Onboarding Modal Series",
    description:
      "Controls whether the onboarding modal series can display in the member app.",
    defaultEnabledForAllUsers: false,
    defaultEnabledForBetaUsers: false,
  },
  {
    key: "voice_chat_ui_v0",
    title: "AI coach voice: UI v0 (modal, no main-chat live text, no Whisper) — default",
    description:
      "When you use voice, the full-screen modal opens. No Whisper, no live mirroring of speech into the main chat. Default. Only one of v0 / v1 / v2 per column.",
    defaultEnabledForAllUsers: true,
    defaultEnabledForBetaUsers: true,
  },
  {
    key: "voice_chat_ui_v1",
    title: "AI coach voice: UI v1 (inline, no modal, no Whisper)",
    description:
      "Voice bar inside the chat (not the full-screen modal), live text in the main thread, no server Whisper (browser STT for user text).",
    defaultEnabledForAllUsers: false,
    defaultEnabledForBetaUsers: false,
  },
  {
    key: "voice_chat_ui_v2",
    title: "AI coach voice: UI v2 (inline, no modal, with Whisper)",
    description:
      "Same layout as v1 (inline, no modal) but OpenAI Realtime input transcription (Whisper) for user audio.",
    defaultEnabledForAllUsers: false,
    defaultEnabledForBetaUsers: false,
  },
  {
    key: "analytics_tab_enabled",
    title: "Progress analytics tab",
    description:
      "Controls visibility of the Progress (analytics) tab in the member sidebar and access to the dashboard analytics page.",
    defaultEnabledForAllUsers: false,
    defaultEnabledForBetaUsers: true,
  },
  {
    key: "member_beta_interface_enabled",
    title: "Member app: beta interface",
    description:
      "When enabled, members use the beta member interface (e.g. related voice and chat layout). When off, the standard layout applies.",
    defaultEnabledForAllUsers: false,
    defaultEnabledForBetaUsers: false,
  },
]

const buildDefaultConfig = () => ({
  version: 1,
  flags: FEATURE_FLAG_DEFINITIONS.reduce((accumulator, definition) => {
    accumulator[definition.key] = {
      enabledForAllUsers: definition.defaultEnabledForAllUsers,
      enabledForBetaUsers: definition.defaultEnabledForBetaUsers,
    }
    return accumulator
  }, {}),
})

const parseConfigValue = (value) => {
  if (!value) return {}
  if (typeof value === "object") return value
  try {
    return JSON.parse(value)
  } catch {
    return {}
  }
}

const normalizeConfig = (value) => {
  const parsed = parseConfigValue(value)
  migrateVoiceChatUiFromLegacy(parsed)
  const defaultConfig = buildDefaultConfig()

  return {
    ...defaultConfig,
    ...parsed,
    flags: FEATURE_FLAG_DEFINITIONS.reduce((accumulator, definition) => {
      const savedFlag = parsed?.flags?.[definition.key] || {}
      accumulator[definition.key] = {
        enabledForAllUsers:
          typeof savedFlag.enabledForAllUsers === "boolean"
            ? savedFlag.enabledForAllUsers
            : definition.defaultEnabledForAllUsers,
        enabledForBetaUsers:
          typeof savedFlag.enabledForBetaUsers === "boolean"
            ? savedFlag.enabledForBetaUsers
            : definition.defaultEnabledForBetaUsers,
      }
      return accumulator
    }, {}),
  }
}

export default function BetaFeatures() {
  const { enqueueSnackbar } = useSnackbar()
  const AccessControl = UsePermissionVerification()
  const [configRow, setConfigRow] = useState(null)
  const [config, setConfig] = useState(buildDefaultConfig())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const canView =
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_LIST]) ||
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_QUERY_BY_KEY]) ||
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_EDIT]) ||
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_ADD])
  const canEdit =
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_EDIT]) ||
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_ADD])

  const loadConfig = useCallback(async () => {
    if (!canView) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const response = await getSystemConfigByKey(CONFIG_KEY)
      verifyResponse(response)
      const row = response?.data || response
      if (row?.id) {
        setConfigRow(row)
        setConfig(normalizeConfig(row?.configValueJson ?? row?.configValue))
      } else {
        setConfigRow(null)
        setConfig(buildDefaultConfig())
      }
    } catch (err) {
      console.error("load beta features config", err)
      setError("Unable to load beta feature settings.")
    } finally {
      setLoading(false)
    }
  }, [canView])

  useEffect(() => {
    loadConfig()
  }, [loadConfig])

  const handleToggle = (flagKey, field) => (event) => {
    const checked = event.target.checked
    setConfig((previous) => ({
      ...previous,
      flags: {
        ...previous.flags,
        [flagKey]: {
          ...previous.flags[flagKey],
          [field]: checked,
        },
      },
    }))
  }

  /** Only one of v0 / v1 / v2 may be on per column; turning one on clears the other two in that column. */
  const handleVoiceChatUiFieldToggle = (activeKey, field) => (event) => {
    const checked = event.target.checked
    setConfig((previous) => {
      const flags = { ...previous.flags }
      const ensureRow = (k) => ({
        enabledForAllUsers: false,
        enabledForBetaUsers: false,
        ...flags[k],
      })
      if (checked) {
        for (const k of VOICE_CHAT_UI_FLAG_KEYS) {
          flags[k] = ensureRow(k)
          flags[k][field] = k === activeKey
        }
      } else {
        flags[activeKey] = { ...ensureRow(activeKey), [field]: false }
        const anyStillOn = VOICE_CHAT_UI_FLAG_KEYS.some((k) => Boolean(flags[k]?.[field]))
        if (!anyStillOn) {
          flags.voice_chat_ui_v0 = { ...ensureRow("voice_chat_ui_v0"), [field]: true }
        }
      }
      return { ...previous, flags }
    })
  }

  const handleSave = async () => {
    if (!canEdit) return

    setSaving(true)
    setError(null)
    const body = {
      name: "Beta feature flags",
      configKey: CONFIG_KEY,
      description:
        "Controls production rollout for beta features, including access for all users and beta testers.",
      configValue: JSON.stringify(config),
    }

    try {
      const response = configRow?.id
        ? await editSystemConfigs({ ...body, id: configRow.id })
        : await addSystemConfigs(body)
      verifyResponse(response)
      enqueueSnackbar("Beta feature settings saved", { variant: "success" })
      await loadConfig()
    } catch (err) {
      console.error("save beta features config", err)
      setError("Failed to save beta feature settings.")
    } finally {
      setSaving(false)
    }
  }

  const enabledSummary = useMemo(
    () =>
      FEATURE_FLAG_DEFINITIONS.reduce(
        (accumulator, definition) => {
          const state = config.flags?.[definition.key] || {}
          if (state.enabledForAllUsers) accumulator.allUsers += 1
          if (state.enabledForBetaUsers) accumulator.betaUsers += 1
          return accumulator
        },
        { allUsers: 0, betaUsers: 0 },
      ),
    [config],
  )

  return (
    <PageContent>
      <BetaFeaturesWrapper>
        <PageHeading>
          <div className="heading-copy">
            <h1 className="page-title">Beta Features</h1>
            <p className="page-subtitle">
              Manage rollout toggles for each named feature. Each flag can be enabled for all users, beta users,
              or both.
            </p>
          </div>
          <div className="actions">
            {canEdit && (
              <Button
                className="save-btn"
                variant="contained"
                onClick={handleSave}
                disabled={saving || loading}
              >
                {saving ? "Saving..." : configRow ? "Update flags" : "Create flags config"}
              </Button>
            )}
          </div>
        </PageHeading>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <div className="page-surface">
          {!canView ? (
            <div className="page-empty">You do not have permission to view beta feature settings.</div>
          ) : (
            <>
              <div className="surface-note">
                <Alert severity="info">
                  Beta users are members with the <strong>Beta tester</strong> switch enabled on their member record.
                </Alert>
                <Chip label={`${enabledSummary.allUsers} flags live for all users`} color="primary" />
                <Chip label={`${enabledSummary.betaUsers} flags live for beta users`} variant="outlined" />
              </div>

              {!canEdit && (
                <Alert severity="warning">
                  You can view the feature flag state here, but your account does not currently have permission to
                  save changes.
                </Alert>
              )}

              <div className="feature-grid">
                {FEATURE_FLAG_DEFINITIONS.map((definition) => {
                  const state = config.flags?.[definition.key] || {}
                  const isVoiceUiGroup = VOICE_CHAT_UI_FLAG_KEYS.includes(definition.key)
                  return (
                    <div className="feature-card" key={definition.key}>
                      <div className="feature-card-header">
                        <div className="feature-title-row">
                          <h2 className="feature-title">{definition.title}</h2>
                          <Chip
                            size="small"
                            color={state.enabledForAllUsers || state.enabledForBetaUsers ? "success" : "default"}
                            label={
                              state.enabledForAllUsers
                                ? "Live"
                                : state.enabledForBetaUsers
                                  ? "Beta only"
                                  : "Off"
                            }
                          />
                        </div>
                        <div className="feature-key">{definition.key}</div>
                        <p className="feature-description">{definition.description}</p>
                        {isVoiceUiGroup ? (
                          <p className="feature-description" style={{ marginTop: 8, fontStyle: "italic" }}>
                            Mutually exclusive with the other two voice UI rows: for each column, only one version is
                            active.
                          </p>
                        ) : null}
                      </div>

                      <div className="toggles">
                        <div className="toggle-row">
                          <div className="toggle-copy">
                            <div className="toggle-label">All users</div>
                            <div className="toggle-help">
                              {isVoiceUiGroup
                                ? "Which voice UI version applies to everyone when this column is on."
                                : "Everyone can access this feature regardless of beta tester status."}
                            </div>
                          </div>
                          <Switch
                            checked={Boolean(state.enabledForAllUsers)}
                            disabled={!canEdit}
                            onChange={
                              isVoiceUiGroup
                                ? handleVoiceChatUiFieldToggle(definition.key, "enabledForAllUsers")
                                : handleToggle(definition.key, "enabledForAllUsers")
                            }
                          />
                        </div>

                        <div className="toggle-row">
                          <div className="toggle-copy">
                            <div className="toggle-label">Beta users</div>
                            <div className="toggle-help">
                              {isVoiceUiGroup
                                ? "Which voice UI version applies to beta testers when the all-users column is off."
                                : "Only members marked as beta testers get this feature when the all-users toggle is off."}
                            </div>
                          </div>
                          <Switch
                            checked={Boolean(state.enabledForBetaUsers)}
                            disabled={!canEdit}
                            onChange={
                              isVoiceUiGroup
                                ? handleVoiceChatUiFieldToggle(definition.key, "enabledForBetaUsers")
                                : handleToggle(definition.key, "enabledForBetaUsers")
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </BetaFeaturesWrapper>
      <LoadingScreen loading={loading} />
    </PageContent>
  )
}
